import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import './ImpactRadarModal.css';
import axios from 'axios';
import { FaUserShield, FaExclamationTriangle, FaTasks, FaCheckCircle, FaLightbulb, FaStar, FaRocket, FaMagic, FaCopy, FaInfoCircle, FaListAlt, FaPlay, FaCheck, FaArrowRight } from 'react-icons/fa';
import { extractStepsByType } from '../utils/testUtils';

// Fonction pour parser les scénarios de test et extraire les étapes Gherkin
const parseTestScenario = (scenario, testObject) => {
  if (!scenario) return null;

  // Si le test a déjà des étapes structurées
  if (testObject?.steps && Array.isArray(testObject.steps)) {
    return {
      steps: testObject.steps.map(step => ({
        type: step.type || 'Given',
        text: step.text || step,
        icon: getStepIcon(step.type || 'Given')
      }))
    };
  }

  // Si scenario est une string, parser les étapes Gherkin
  if (typeof scenario === 'string') {
    const lines = scenario.split('\n').map(l => l.trim()).filter(Boolean);
    const steps = [];

    for (const line of lines) {
      // Détecter les mots-clés Gherkin (anglais et français)
      const match = line.match(/^(Given|When|Then|And|But|Étant donné|Quand|Alors|Et)\s+(.+)/i);
      if (match) {
        const type = normalizeStepType(match[1]);
        const text = match[2];
        steps.push({
          type,
          text,
          icon: getStepIcon(type)
        });
      } else if (line.length > 0 && !line.startsWith('Scenario:') && !line.startsWith('Feature:')) {
        // Ligne sans mot-clé, traiter comme une étape générique
        steps.push({
          type: 'Step',
          text: line,
          icon: '📝'
        });
      }
    }

    if (steps.length > 0) {
      return { steps };
    }
  }

  // Fallback : extraire depuis la description du test
  if (testObject?.description) {
    try {
      const extractedSteps = extractStepsByType(testObject);
      if (extractedSteps && extractedSteps.length > 0) {
        const steps = extractedSteps.map(stepString => {
          const match = String(stepString).match(/^(Given|When|Then|And|But):\s*(.+)$/i);
          if (match) {
            const type = normalizeStepType(match[1]);
            return {
              type,
              text: match[2],
              icon: getStepIcon(type)
            };
          }
          return {
            type: 'Step',
            text: String(stepString),
            icon: '📝'
          };
        });
        return { steps };
      }
    } catch (e) {
      console.warn('Erreur extraction steps:', e);
    }
  }

  return null;
}

// Normaliser les types d'étapes
const normalizeStepType = (type) => {
  const normalized = type.toLowerCase();
  if (normalized === 'étant donné' || normalized === 'given') return 'Given';
  if (normalized === 'quand' || normalized === 'when') return 'When';
  if (normalized === 'alors' || normalized === 'then') return 'Then';
  if (normalized === 'et' || normalized === 'and') return 'And';
  if (normalized === 'but') return 'But';
  return 'Step';
};

// Obtenir l'icône pour chaque type d'étape
const getStepIcon = (type) => {
  switch (type) {
    case 'Given': return '📖'; // Contexte/Préconditions
    case 'When': return '▶️'; // Action
    case 'Then': return '✅'; // Vérification/Résultat attendu
    case 'And': return '➕'; // Étape additionnelle
    case 'But': return '❌'; // Cas d'exception
    default: return '📝'; // Étape générique
  }
};

// Composant pour afficher un scénario de test structuré
const TestScenarioDisplay = ({ scenario, title, testObject }) => {
  const parsedScenario = parseTestScenario(scenario, testObject);

  if (!parsedScenario || !parsedScenario.steps || parsedScenario.steps.length === 0) {
    // Affichage simple si pas de structure détectée
    return (
      <div className="irmodal-test-scenario">
        <div className="irmodal-test-scenario-title">
          <FaTasks className="irmodal-scenario-icon" />
          <strong>{t('impactRadar.testScenarios.title')}</strong>
        </div>
        <div className="irmodal-test-scenario-content">
          {scenario || testObject?.description || t('impactRadar.noScenario')}
        </div>
      </div>
    );
  }

  return (
    <div className="irmodal-test-scenario-structured">
      <div className="irmodal-test-scenario-title">
        <FaListAlt className="irmodal-scenario-icon" />
        <strong>{t('impactRadar.testScenarios.title')}</strong>
      </div>
      <div className="irmodal-test-scenario-steps">
        {parsedScenario.steps.map((step, idx) => (
          <div key={idx} className={`irmodal-test-step irmodal-step-${step.type.toLowerCase()}`}>
            <span className="irmodal-step-icon">{step.icon}</span>
            <span className="irmodal-step-text">{step.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Composant pour le bouton de copie
const CopyButton = ({ textToCopy, children, className = "", ...props }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      console.log('✅ Texte copié dans le presse-papiers');
    } catch (err) {
      console.error('❌ Erreur lors de la copie:', err);
      // Fallback pour les navigateurs qui ne supportent pas navigator.clipboard
      try {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        console.log('✅ Texte copié via fallback');
      } catch (fallbackErr) {
        console.error('❌ Échec du fallback de copie:', fallbackErr);
      }
    }
  };

  return (
    <button className={className} onClick={handleCopy} {...props}>
      {copied ? '✅ Copié!' : children}
    </button>
  );
};

// Fonction pour créer des steps Gherkin bien formatés à partir d'un test
const createGherkinStepsFromTest = (test) => {
  let gherkinSteps = [];

  // 1. Essayer d'extraire depuis test.steps s'ils existent déjà
  if (test.steps && Array.isArray(test.steps) && test.steps.length > 0) {
    // Si les steps sont déjà au bon format {type, text}
    if (typeof test.steps[0] === 'object' && test.steps[0].type && test.steps[0].text) {
      console.log('✅ Steps déjà au bon format:', test.steps);
      return test.steps;
    }

    // Si les steps sont des strings, les parser
    if (typeof test.steps[0] === 'string') {
      gherkinSteps = test.steps.map(stepString => {
        const match = stepString.match(/^(Given|When|Then|And|But)\s*[:\-]?\s*(.+)$/i);
        if (match) {
          return {
            type: normalizeStepType(match[1]),
            text: match[2].trim()
          };
        } else {
          return {
            type: 'Given',
            text: stepString
          };
        }
      });
    }
  }

  // 2. Essayer d'extraire depuis test.scenario
  if ((!gherkinSteps || gherkinSteps.length === 0) && test.scenario) {
    if (typeof test.scenario === 'string') {
      const lines = test.scenario.split('\n').map(l => l.trim()).filter(Boolean);
      gherkinSteps = lines.map(line => {
        const match = line.match(/^(Given|When|Then|And|But|Étant donné|Quand|Alors|Et|Mais)\s+(.+)/i);
        if (match) {
          return {
            type: normalizeStepType(match[1]),
            text: match[2].trim()
          };
        } else {
          return {
            type: 'Given',
            text: line
          };
        }
      });
    }
  }

  // 3. Essayer d'extraire depuis test.description
  if ((!gherkinSteps || gherkinSteps.length === 0) && test.description) {
    const lines = test.description.split('\n').map(l => l.trim()).filter(Boolean);
    gherkinSteps = lines.map((line, index) => {
      const match = line.match(/^(Given|When|Then|And|But|Étant donné|Quand|Alors|Et|Mais)\s+(.+)/i);
      if (match) {
        return {
          type: normalizeStepType(match[1]),
          text: match[2].trim()
        };
      } else {
        // Inférer le type basé sur la position
        let inferredType = 'Given';
        if (index === 1) inferredType = 'When';
        if (index === 2) inferredType = 'Then';
        if (index > 2) inferredType = 'And';

        return {
          type: inferredType,
          text: line
        };
      }
    });
  }

  // 4. Fallback : créer des steps génériques à partir du titre et description
  if (!gherkinSteps || gherkinSteps.length === 0) {
    const title = test.title || test.name || t('impactRadar.undefinedTest');
    const description = test.description || '';

    gherkinSteps = [
      { type: 'Given', text: t('impactRadar.fallbackGiven', { title }) },
      { type: 'When', text: description || t('impactRadar.fallbackWhen', { title }) },
      { type: 'Then', text: t('impactRadar.fallbackThen') }
    ];
  }

  console.log(`✅ Steps Gherkin créés pour "${test.title || test.name}":`, gherkinSteps);
  return gherkinSteps;
};

// Fonction pour traduire les types d'impact
const getImpactTypeLabel = (impactType, t) => {
  const impactTypeMap = {
    'core-feature': t('impactRadar.impactTypes.coreFeature'),
    'test-coverage-missing': t('impactRadar.impactTypes.testCoverageMissing'), 
    'test-obsolete': t('impactRadar.impactTypes.testObsolete'),
    'dependency-deep': t('impactRadar.impactTypes.dependencyDeep'),
    'direct': t('impactRadar.impactTypes.direct'),
    'indirect': t('impactRadar.impactTypes.indirect'),
    'Feature': t('impactRadar.impactTypes.feature')
  };
  return impactTypeMap[impactType] || impactType;
};

// Fonction pour traduire les priorités de test
const getTestPriorityLabel = (priority, t) => {
  const priorityMap = {
    'low': t('impactRadar.testPriorities.low'),
    'medium': t('impactRadar.testPriorities.medium'),
    'high': t('impactRadar.testPriorities.high'),
    'critical': t('impactRadar.testPriorities.critical')
  };
  return priorityMap[priority] || priority;
};

// Fonction pour traduire les types de test  
const getTestTypeLabel = (type, t) => {
  const typeMap = {
    'functional': t('impactRadar.testTypes.functional'),
    'integration': t('impactRadar.testTypes.integration'),
    'unit': t('impactRadar.testTypes.unit'),
    'acceptance': t('impactRadar.testTypes.acceptance'),
    'performance': t('impactRadar.testTypes.performance'),
    'security': t('impactRadar.testTypes.security')
  };
  return typeMap[type] || type;
};

const ImpactRadarModal = ({ 
  open, 
  onClose, 
  impactRadarResult, 
  impactTypeFilter, 
  setImpactTypeFilter, 
  pendingNodes = [],
  onConfirmModification = null,
  pendingModificationText
}) => {
  const { t } = useTranslation();
  
  // Use translation for default text
  const defaultModificationText = pendingModificationText || t('impactRadar.continueModification');
  const [showLastImpactRadar, setShowLastImpactRadar] = useState(false);
  const [lastImpactRadarResult, setLastImpactRadarResult] = useState(null);
  const [loadingLastImpactRadar, setLoadingLastImpactRadar] = useState(false);

  const fetchLastImpactRadarAnalysis = async () => {
    setLoadingLastImpactRadar(true);
    try {
      const res = await axios.get('/session/impact-radar', { withCredentials: true });
      setLastImpactRadarResult(res.data);
      setShowLastImpactRadar(true);
    } catch (err) {
      toast.info(t('impactRadar.noSavedAnalysis'));
      setLastImpactRadarResult(null);
      setShowLastImpactRadar(false);
    }
    setLoadingLastImpactRadar(false);
  };

  if (!open) return null;

  // Si on est en mode loading, afficher un spinner avec étapes
  if (!impactRadarResult && !showLastImpactRadar) {
    return (
      <div className="impact-radar-modal-root irmodal-overlay" style={{background: 'radial-gradient(circle at 60% 40%, #6366f1 0%, #eef2ff 100%)'}}>
        <div className="irmodal-card animate-wahoo" style={{boxShadow: '0 12px 48px rgba(102,126,234,0.18)', borderRadius: 32, border: '2px solid #6366f1', position: 'relative', overflow: 'hidden'}}>
          <button className="irmodal-close" onClick={onClose} style={{top: 18, right: 18, fontSize: 24, background: 'rgba(255,255,255,0.85)', borderRadius: '50%', boxShadow: '0 2px 12px rgba(99,102,241,0.12)'}}>✖</button>
          {loadingLastImpactRadar ? (
            <div className="flex flex-col items-center justify-center min-h-[120px] p-6">
              <div className="btn-spinner" style={{ width: 64, height: 64, borderWidth: 7, marginBottom: 22, boxShadow: '0 0 0 8px #e0e7ff' }}></div>
              <div className="text-lg font-bold text-indigo-700 mb-2 tracking-wide">Chargement de la dernière analyse...</div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[320px] p-10" style={{width:'100%'}}>
              {/* Timeline verticale animée */}
              <div style={{position:'absolute',left:'50%',top:60,transform:'translateX(-50%)',height:'220px',width:'6px',background:'linear-gradient(180deg,#6366f1 0%,#a5b4fc 100%)',borderRadius:'8px',zIndex:0,opacity:0.18}}></div>
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',zIndex:1}}>
                <div style={{marginBottom:18}}>
                  <span style={{fontSize:48,background:'linear-gradient(90deg,#6366f1,#a5b4fc)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>🔍</span>
                </div>
                <div style={{marginBottom:18}}>
                  <span style={{fontSize:48,background:'linear-gradient(90deg,#6366f1,#a5b4fc)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>⚡</span>
                </div>
                <div style={{marginBottom:18}}>
                  <span style={{fontSize:48,background:'linear-gradient(90deg,#6366f1,#a5b4fc)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>📊</span>
                </div>
              </div>
              <div className="text-2xl font-extrabold text-indigo-700 mb-3 tracking-wide" style={{letterSpacing: '1px',marginTop:12}}>{t('impactRadar.analysisInProgress')}</div>
              <div className="text-base text-gray-600 mb-6" style={{fontSize:'1.15em'}} dangerouslySetInnerHTML={{ __html: t('impactRadar.analysisDescription') }} />
              <div className="w-full max-w-md mb-2">
                <div className="flex flex-col gap-2 text-xs text-gray-600 mb-2" style={{alignItems:'flex-start'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}><span style={{fontSize:22}}>🔍</span> <span style={{fontWeight:600}}>{t('impactRadar.graphExploration')}</span></div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}><span style={{fontSize:22}}>⚡</span> <span style={{fontWeight:600}}>{t('impactRadar.aiAnalysis')}</span></div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}><span style={{fontSize:22}}>📊</span> <span style={{fontWeight:600}}>{t('impactRadar.synthesisRecommendations')}</span></div>
                </div>
                <div className="w-full bg-gradient-to-r from-indigo-200 via-indigo-400 to-indigo-600 rounded-full h-4 shadow-lg" style={{marginBottom:10}}>
                  <div className="bg-indigo-600 h-4 rounded-full animate-pulse" style={{ width: '60%', boxShadow:'0 2px 12px #6366f1' }}></div>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-2 italic" style={{fontSize:'1em'}} dangerouslySetInnerHTML={{ __html: t('impactRadar.analysisTime') }} />
              <div className="text-sm text-indigo-500 mt-4 font-semibold" style={{textAlign:'center'}}>{t('impactRadar.patienceMessage')}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Afficher la dernière analyse si demandée
  if (showLastImpactRadar && lastImpactRadarResult) {
    // Réutilise le rendu principal avec lastImpactRadarResult
    impactRadarResult = lastImpactRadarResult;
  }
  // Remove stray JSX code outside of return statement

  // Affiche la dernière analyse si demandée
  if (showLastImpactRadar && lastImpactRadarResult) {
    return (
      <div className="impact-radar-modal-root irmodal-overlay">
        <div className="irmodal-card animate-wahoo">
          <button className="irmodal-close" onClick={() => setShowLastImpactRadar(false)}>✖</button>
          {/* ...reprendre l'affichage existant en utilisant lastImpactRadarResult à la place de impactRadarResult... */}
          {/* ...existing code for modal content, replace impactRadarResult by lastImpactRadarResult... */}
        </div>
      </div>
    );
  }

  // Définir criticalTests avec un parsing amélioré des steps Gherkin
  const rawCriticalTests = impactRadarResult?.planTests || impactRadarResult?.llmSuggestions?.criticalTestsToGenerate || impactRadarResult?.criticalTestsToGenerate || [];
  const criticalTests = Array.isArray(rawCriticalTests)
    ? rawCriticalTests.map(test => ({
      ...test,
      steps: createGherkinStepsFromTest(test)
    }))
    : [];

  // Helper pour savoir si un test est déjà en cours de modélisation (par nom ou titre)
  const isTestPending = (test) => {
    if (!test) return false;
    return pendingNodes.some(
      n => (n.name && (n.name === (test.name || test.title)))
    );
  };

  // Champs enrichis LLM (compatibilité descendante + nouveaux champs)
  const llm = impactRadarResult.llmSuggestions || {};
  const impactSummary = impactRadarResult.impactSummary || llm.impactSummary || llm.summary || t('impactRadar.analysisInProgress');
  const recommendations = impactRadarResult.recommendations || llm.recommendations || impactRadarResult.recommendations || [];
  const nodeScores = llm.nodeScores || impactRadarResult.nodeScores || [];
  const technicalExplanation = llm.technicalExplanation;
  const filtersSummary = llm.filtersSummary || impactRadarResult.filtersSummary || {};
  const relations = llm.relations || impactRadarResult.relations || [];
  const criticalPaths = llm.criticalPaths || impactRadarResult.criticalPaths || [];
  const riskHotspots = llm.riskHotspots || impactRadarResult.riskHotspots || [];
  const deploymentStrategy = llm.deploymentStrategy || impactRadarResult.rollbackPlan;
  const monitoringRecommendations = impactRadarResult.monitoring || llm.monitoringRecommendations || [];
  const rollbackPlan = impactRadarResult.rollbackPlan || llm.rollbackPlan;
  const qualityGates = llm.qualityGates || [];
  const effortEstimation = impactRadarResult.effortEstimation || llm.effortEstimation || {};
  const calculatedMetrics = llm.calculatedMetrics || {};
  const coachTips = impactRadarResult.coachTips || [];

  const impactedNodes = (impactRadarResult.impactedNodes || []).map(node => ({
    ...node,
    name: node.name || t('impactRadar.unknownNode'),
    type: node.type || node.nodeType || t('impactRadar.unknownType')
  }));

  // Helper to translate values that may be either:
  // - a plain string (could be a translation key like 'impactRadar.backend.xxx')
  // - an object like { i18nKey, i18nParams }
  // - an object like { key, params } (new format from backend)
  // - an object with a legacy string { legacy }
  // Returns a string safe to render.
  const translateMaybe = (value) => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string') {
      // Si ça ressemble à une clé de traduction (contient des points et commence par une section connue)
      if (value.includes('.') && (value.startsWith('impactRadar.') || value.startsWith('featureManager.') || value.startsWith('actions.') || value.startsWith('placeholders.'))) {
        try {
          return t(value);
        } catch (e) {
          // Si la traduction échoue, retourner la valeur originale
          return value;
        }
      }
      return value;
    }
    if (typeof value === 'object') {
      // Nouveau format du backend avec i18nKey
      if (value.i18nKey && typeof value.i18nKey === 'string') {
        try {
          return t(value.i18nKey, value.i18nParams || {});
        } catch (e) {
          return value.i18nKey;
        }
      }
      // Format alternatif key/params
      if (value.key && typeof value.key === 'string') {
        try {
          return t(value.key, value.params || {});
        } catch (e) {
          return value.key;
        }
      }
      // Ancien format legacy
      if (value.legacy) return value.legacy;
      if (value.text) return value.text;
      // Last resort: stringify
      try { return String(value); } catch (e) { return JSON.stringify(value); }
    }
    return String(value);
  };

  const translateRec = (rec) => {
    if (!rec) return rec;
    if (typeof rec === 'string') return rec;
    if (rec.action) return translateMaybe(rec.action);
    return translateMaybe(rec);
  };

  // Génère un rapport texte enrichi à copier
  const getFormattedReport = () => {
    if (!impactRadarResult) {
      return t('impactRadar.noAnalysisDataForCopy');
    }
    
    let report = '';
    report += `=== ${t('impactRadar.reportTitle')} ===\n`;
    report += `${t('impactRadar.analysisDate')}: ${new Date().toLocaleString()}\n`;
    if (impactSummary) report += `${t('impactRadar.summary')}: ${translateMaybe(impactSummary)}\n\n`;

    // Ajouter les nœuds impactés
    if (impactRadarResult.impactedNodes && impactRadarResult.impactedNodes.length > 0) {
      report += `\n=== ${t('impactRadar.stats.impactedNodes')} ===\n`;
      report += `${t('impactRadar.totalImpactedNodes')}: ${impactRadarResult.impactedNodes.length}\n\n`;
      impactRadarResult.impactedNodes.forEach((node, idx) => {
        report += `${idx + 1}. ${node.name || t('impactRadar.unknownNode')}\n`;
        if (node.type) report += `   Type: ${node.type}\n`;
        if (node.impactType) report += `   Impact: ${getImpactTypeLabel(node.impactType, t)}\n`;
        if (node.riskScore !== undefined) report += `   ${t('impactRadar.stats.riskScore')}: ${node.riskScore}\n`;
        if (node.criticity) report += `   ${t('impactRadar.criticality')}: ${node.criticity}\n`;
        if (node.riskExplanation) report += `   Why: ${translateMaybe(node.riskExplanation)}\n`;
        if (node.suggestedAction) report += `   Action: ${translateMaybe(node.suggestedAction)}\n`;
        report += '\n';
      });
    }

    if (recommendations.length > 0) {
      report += `\n=== ${t('impactRadar.recommendations.title')} ===\n`;
      recommendations.forEach((rec, i) => {
        const recText = translateRec(rec);
        report += `  - ${recText}\n`;
      });
      report += '\n';
    }

    if (coachTips && coachTips.length > 0) {
      report += `\n=== ${t('impactRadar.recommendations.coachTips')} ===\n`;
      coachTips.forEach((tip) => {
        report += `  💡 ${translateMaybe(tip)}\n`;
      });
      report += '\n';
    }

    report += `\n=== ${t('impactRadar.stats.metrics')} ===\n`;
    report += `${t('impactRadar.stats.riskScore')}: ${impactRadarResult.riskScore}\n`;
    report += `${t('impactRadar.maxDepth')}: ${impactRadarResult.maxDepth || 1}\n`;
    report += `${t('impactRadar.criticalNodesCount')}: ${impactRadarResult.criticalCount || 0}\n`;
    report += `${t('impactRadar.confidenceScore')}: ${impactRadarResult.confidenceScore || 0}%\n`;

    if (monitoringRecommendations && monitoringRecommendations.length > 0) {
      report += `\n=== ${t('impactRadar.monitoringRecommendations')} ===\n`;
      monitoringRecommendations.forEach((rec) => {
        report += `  - ${translateMaybe(rec)}\n`;
      });
      report += '\n';
    }

    if (rollbackPlan) {
      report += `\n${t('impactRadar.rollbackPlan')}: ${translateMaybe(rollbackPlan)}\n`;
    }

    if (effortEstimation && Object.keys(effortEstimation).length > 0) {
      report += `\n=== ${t('impactRadar.effortEstimation')} ===\n`;
      Object.entries(effortEstimation).forEach(([k, v]) => {
        report += `  - ${k}: ${v}\n`;
      });
      report += '\n';
    }

    if (criticalTests && criticalTests.length > 0) {
      report += `\n=== ${t('impactRadar.criticalTests')} (${criticalTests.length}) ===\n`;
      criticalTests.forEach((test, idx) => {
        report += `${idx + 1}. ${translateMaybe(test.name || test.title || `Test #${idx + 1}`)}\n`;
        if (test.description) report += `   ${translateMaybe(test.description)}\n`;
        if (test.priority) report += `   Priority: ${getTestPriorityLabel(test.priority, t)}\n`;
        if (test.type) report += `   Type: ${getTestTypeLabel(test.type, t)}\n`;
        report += '\n';
      });
    }

    if (impactRadarResult.ritualSuggested) {
      report += `\n${t('impactRadar.suggestedRitual')}: ${translateMaybe(impactRadarResult.ritualSuggested)}`;
      if (impactRadarResult.assignedTo) report += ` (${t('impactRadar.assignedTo')} ${translateMaybe(impactRadarResult.assignedTo)})`;
      report += '\n';
    }
    
    report += `\n=== ${t('impactRadar.endReport')} ===\n`;
    return report;
  };

  // Affichage enrichi en haut de la modale
  const renderLlmEnrichedBlocks = () => (
    <>
      {/* Résumé naturel de l'impact */}
      {impactSummary && (
        <div className="irmodal-summary irmodal-block">
          <FaInfoCircle className="irmodal-summary-icon" />
          <span className="irmodal-summary-text">{translateMaybe(impactSummary)}</span>
        </div>
      )}

      {/* Explication technique */}
      {technicalExplanation && (
        <div className="irmodal-tech-explanation irmodal-block">
          <FaInfoCircle className="irmodal-summary-icon" />
          <span className="irmodal-summary-text">{translateMaybe(technicalExplanation)}</span>
        </div>
      )}

      {/* Recommandations */}
      {recommendations.length > 0 && (
        <div className="irmodal-recommendations irmodal-block">
          <FaListAlt className="irmodal-reco-icon" />
          <span className="irmodal-reco-title">{t('impactRadar.recommendations.title')}</span>
          <ul className="irmodal-reco-list">
            {recommendations.map((rec, i) => (
              <li key={i}>{translateRec(rec)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Coach Tips */}
      {coachTips && coachTips.length > 0 && (
        <div className="irmodal-coach-tips irmodal-block">
          <FaLightbulb className="irmodal-coach-icon" />
          <span className="irmodal-coach-title">{t('impactRadar.recommendations.coachTips')}</span>
          <ul className="irmodal-coach-list">
            {coachTips.map((tip, i) => (
              <li key={i}>{translateMaybe(tip)}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  return (
    <div className="impact-radar-modal-root irmodal-overlay">
      <div className="irmodal-card animate-wahoo">
        <button className="irmodal-close" onClick={onClose}>✖</button>
        <CopyButton textToCopy={getFormattedReport()} className="irmodal-btn-copy">
          <FaCopy style={{ marginRight: 6, verticalAlign: 'middle' }} /> {t('impactRadar.copyButton')}
        </CopyButton>
        <div className="irmodal-header">
          <span className="irmodal-icon"><FaMagic style={{ color: '#fff' }} /></span>
          <div>
            <h2 className="irmodal-title gradient-text">{t('impactRadar.title')}</h2>
            <p className="irmodal-desc">{t('impactRadar.description')} <FaRocket className="irmodal-rocket" /></p>
          </div>
        </div>

        {/* Blocs enrichis LLM (résumé, reco, scores) */}
        {renderLlmEnrichedBlocks()}

        <div className="irmodal-stats irmodal-stats-cards">
          <div className="irmodal-stat-card irmodal-risk">
            <span className="irmodal-stat-icon"><FaExclamationTriangle /></span>
            <span className="irmodal-stat-label">{t('impactRadar.stats.riskScore')}</span>
            <span className="irmodal-stat-value irmodal-risk-score rainbow-text">{impactRadarResult.riskScore}</span>
          </div>
          <div className="irmodal-stat-card irmodal-depth">
            <span className="irmodal-stat-icon"><FaRocket /></span>
            <span className="irmodal-stat-label">{t('impactRadar.stats.impactedNodes')}</span>
            <span className="irmodal-stat-value">{impactRadarResult.nodeCount || impactedNodes.length}</span>
          </div>
          <div className="irmodal-stat-card irmodal-critical">
            <span className="irmodal-stat-icon"><FaUserShield /></span>
            <span className="irmodal-stat-label">{t('impactRadar.stats.highCriticality')}</span>
            <span className="irmodal-stat-value">{impactRadarResult.criticalCount || 0}</span>
          </div>
          <div className="irmodal-stat-card irmodal-tests">
            <span className="irmodal-stat-icon"><FaTasks /></span>
            <span className="irmodal-stat-label">{t('impactRadar.stats.requiredTests')}</span>
            <span className="irmodal-stat-value">{criticalTests.length}</span>
          </div>
          {impactRadarResult.fromCache && (
            <div className="irmodal-stat-card irmodal-cache">
              <span className="irmodal-stat-icon">⚡</span>
              <span className="irmodal-stat-label">{t('impactRadar.stats.acceleratedAnalysis')}</span>
              <span className="irmodal-stat-value">Cache</span>
            </div>
          )}
        </div>

        <div className="irmodal-section irmodal-section-nodes">
          <h3 className="irmodal-section-title"><FaStar className="irmodal-star" /> {t('impactRadar.stats.impactedNodes')}</h3>
          <div className="irmodal-nodes-list">
            {impactedNodes.length === 0 && <div className="irmodal-empty">{t('impactRadar.noImpactedNodes')}</div>}
            {impactedNodes.map((n, i) => (
              <div key={i} className={`irmodal-node-card irmodal-node-${n.impactType}`}>
                <span className="irmodal-node-icon">
                  {n.impactType === "core-feature" && <FaUserShield title={t('impactRadar.coreFeature')} />}
                  {n.impactType === "test-coverage-missing" && <FaExclamationTriangle title={t('impactRadar.missingTestCoverage')} />}
                  {n.impactType === "test-obsolete" && <FaTasks title={t('impactRadar.obsoleteTest')} />}
                  {n.impactType === "dependency-deep" && <FaRocket title={t('impactRadar.deepDependency')} />}
                  {n.impactType === "direct" && <FaCheckCircle title={t('impactRadar.directImpact')} />}
                  {n.impactType === "indirect" && <FaLightbulb title={t('impactRadar.indirectImpact')} />}
                </span>
                <div className="irmodal-node-info">
                  <div className="irmodal-node-name">{n.name}</div>
                  <div className="irmodal-node-meta">
                    <span className="irmodal-badge">{getImpactTypeLabel(n.impactType, t)}</span>
                    {n.riskScore && <span className="irmodal-risk-mini">{t('impactRadar.risk')}: {n.riskScore}</span>}
                    {n.criticity && <span className="irmodal-criticity-mini">{t('impactRadar.criticality')}: {n.criticity}</span>}
                  </div>
                  {n.riskExplanation && <div className="irmodal-risk-explanation"><strong>{t('impactRadar.why')}?</strong> {translateMaybe(n.riskExplanation)}</div>}
                  {n.suggestedAction && <div className="irmodal-action"><FaLightbulb /> <strong>{t('impactRadar.action')}:</strong> {translateMaybe(n.suggestedAction)}</div>}
                  {(n.associatedTests || n.testsAssociated)?.length > 0 && <div className="irmodal-tests">{t('impactRadar.associatedTests')}: {(n.associatedTests || n.testsAssociated).join(", ")}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="irmodal-section irmodal-section-tests">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 className="irmodal-section-title"><FaTasks className="irmodal-tasks" /> {t('impactRadar.criticalTests')}</h3>
            {criticalTests.length > 0 && criticalTests.some(t => !isTestPending(t)) ? (
              <button
                className="irmodal-btn-modeling irmodal-btn-add-all"
                onClick={() => {
                  const toAdd = criticalTests.filter(t => !isTestPending(t));
                  if (toAdd.length > 0) {
                    // Formatage des tests avec scénarios Gherkin bien définis et targetNode standardisé
                    const testsToSend = toAdd.map((test, i) => {
                      // 🔧 STANDARDISATION: S'assurer que chaque test a un targetNode
                      const targetNode = test.targetNode || 
                                        test.nodeName || 
                                        test.associatedNodes?.[0] || 
                                        test.nodesInvolved?.[0] ||
                                        impactedNodes?.[0]?.name; // 👈 NOUVEAU: Fallback sur le premier nœud impacté

                      // Créer les scénarios à partir des steps
                      const scenarios = [];
                      // Détermination du titre comme dans l'affichage
                      const rawTitle = test.name || test.title ||
                        (test.steps && Array.isArray(test.steps) && test.steps.length > 0 && test.steps[0].text)
                        || (test.scenario && typeof test.scenario === 'string' && test.scenario.split('\n')[0])
                        || `Test #${i + 1}`;
                      const testTitle = translateMaybe(rawTitle);
                      if (test.steps && Array.isArray(test.steps) && test.steps.length > 0) {
                        scenarios.push({
                          title: testTitle,
                          type: test.type || 'acceptance',
                          steps: test.steps,
                          positive: true,
                          testType: 'positive',
                          category: 'functional'
                        });
                      }

                      return {
                        ...test,
                        title: testTitle,
                        targetNode: targetNode, // 👈 CRUCIAL: Toujours présent
                        nodeName: targetNode,   // 👈 Alias pour compatibilité
                        scenarios: scenarios,
                        metadata: {
                          ...test.metadata,
                          sourceType: 'impact-radar',
                          targetNode: targetNode, // 👈 Aussi dans les métadonnées
                          originalTest: test
                        },
                        gherkin: test.steps && Array.isArray(test.steps) ?
                          test.steps.map(s => `${s.type} ${s.text}`).join('\n') : ''
                      };
                    });

                    console.log('🧪 Envoi de tests vers "en cours de modélisation":', testsToSend);
                    window.dispatchEvent(new CustomEvent('addToModeling', { detail: { tests: testsToSend } }));
                  }
                }}
                title={t('impactRadar.addAllTestsTitle')}
              >
                <FaTasks style={{ marginRight: '6px' }} />
                {t('impactRadar.addAllTests')} ({criticalTests.filter(t => !isTestPending(t)).length})
              </button>
            ) : criticalTests.length > 0 ? (
              <span style={{ color: '#10b981', fontWeight: 600, marginLeft: 12 }}>{t('impactRadar.allTestsAlreadyAdded')}</span>
            ) : null}
          </div>

          <div className="irmodal-tests-list">
            {criticalTests.length === 0 && <div className="irmodal-empty">{t('impactRadar.noTestsToGenerate')}</div>}
            {criticalTests.map((test, i) => (
              <div key={test.name || test.title || test.id || i} className="irmodal-test-card">
                <div className="irmodal-test-header">
                  <FaTasks className="irmodal-test-icon" />
                  <span className="irmodal-test-title">{
                    translateMaybe(test.name || test.title ||
                    (test.steps && Array.isArray(test.steps) && test.steps.length > 0 && test.steps[0].text)
                    || (test.scenario && typeof test.scenario === 'string' && test.scenario.split('\n')[0])
                    || `Test #${i + 1}`)
                  }</span>
                  {test.priority && <span className={`irmodal-badge irmodal-badge-priority irmodal-badge-${test.priority}`}>{getTestPriorityLabel(test.priority, t)}</span>}
                  {test.type && <span className={`irmodal-badge irmodal-badge-type irmodal-badge-${test.type}`}>{getTestTypeLabel(test.type, t)}</span>}
                  <span className="irmodal-test-info" title="Ce test doit être revu en 3 amigos (Dev, QA, PO) pour garantir la qualité et la pertinence."><FaInfoCircle /></span>
                </div>

                {/* Affichage de la description principale */}
                {test.description && (
                  <div className="irmodal-test-desc">
                    <strong>{t('impactRadar.testDescription')}</strong> {translateMaybe(test.description)}
                  </div>
                )}

                {/* Affichage des étapes Gherkin structurées */}
                {test.steps && Array.isArray(test.steps) && test.steps.length > 0 && (
                  <div className="irmodal-test-scenario-structured">
                    <div className="irmodal-test-scenario-title">
                      <FaListAlt className="irmodal-scenario-icon" />
                      <strong>{t('impactRadar.testScenarios.gherkinTitle')}</strong>
                    </div>
                    <div className="irmodal-test-scenario-steps">
                      {test.steps.map((step, idx) => (
                        <div key={idx} className={`irmodal-test-step irmodal-step-${step.type?.toLowerCase() || 'step'}`}> 
                          <span className="irmodal-step-icon">{getStepIcon(step.type)}</span>
                          <span className="irmodal-step-keyword">{step.type}:</span>
                          <span className="irmodal-step-text">{step.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {test.expectedOutcome && <div className="irmodal-test-outcome"><strong>{t('impactRadar.expectedResult')}</strong> {translateMaybe(test.expectedOutcome)}</div>}
                {(test.associatedNodes || test.nodesInvolved)?.length > 0 && <div className="irmodal-test-nodes">{t('impactRadar.involvedNodes')}: {(test.associatedNodes || test.nodesInvolved).join(", ")}</div>}
                {test.action && <div className="irmodal-test-action"><FaLightbulb /> {translateMaybe(test.action)}</div>}

                {isTestPending(test) ? (
                  <div className="irmodal-test-status irmodal-test-status-sent">
                    <span className="irmodal-status-icon" style={{color:'#10b981',fontSize:'1.2em',marginRight:6}}><FaCheck /></span>
                    <span className="irmodal-status-text" style={{color:'#10b981',fontWeight:600}}>{t('impactRadar.alreadySentToModeling')}</span>
                  </div>
                ) : (
                  <button className="irmodal-btn-modeling" onClick={() => {
                    // 🔧 STANDARDISATION: S'assurer que chaque test a un targetNode
                    const targetNode = test.targetNode || 
                                      test.nodeName || 
                                      test.associatedNodes?.[0] || 
                                      test.nodesInvolved?.[0] ||
                                      impactedNodes?.[0]?.name; // 👈 NOUVEAU: Fallback sur le premier nœud impacté

                    // Formatage du test individuel avec scénarios Gherkin
                    const scenarios = [];
                    if (test.steps && Array.isArray(test.steps) && test.steps.length > 0) {
                      scenarios.push({
                        title: test.name || test.title || t('impactRadar.mainScenario'),
                        type: test.type || 'acceptance',
                        steps: test.steps,
                        positive: true,
                        testType: 'positive',
                        category: 'functional'
                      });
                    }

                    const testToSend = {
                      ...test,
                      targetNode: targetNode, // 👈 CRUCIAL: Toujours présent
                      nodeName: targetNode,   // 👈 Alias pour compatibilité
                      scenarios: scenarios,
                      metadata: {
                        ...test.metadata,
                        sourceType: 'impact-radar',
                        targetNode: targetNode, // 👈 Aussi dans les métadonnées
                        originalTest: test
                      },
                      gherkin: test.steps && Array.isArray(test.steps) ?
                        test.steps.map(s => `${s.type} ${s.text}`).join('\n') : ''
                    };

                    console.log(`🧪 Envoi d'un test individuel vers "en cours de modélisation":`, testToSend);
                    window.dispatchEvent(new CustomEvent('addToModeling', { detail: { tests: [testToSend] } }));
                  }}>
                    {t('impactRadar.addToModeling')}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {impactRadarResult.ritualSuggested && (
          <div className="irmodal-ritual">
            <FaMagic className="irmodal-ritual-icon" />
            <strong>{t('impactRadar.suggestedRitual')}</strong> {translateMaybe(impactRadarResult.ritualSuggested)} ({t('impactRadar.assignedTo')} {translateMaybe(impactRadarResult.assignedTo)})
          </div>
        )}

        <div className="irmodal-footer">
          <button className="irmodal-btn-close" onClick={onClose}>{t('common.close')}</button>
          {onConfirmModification && (
            <button 
              className="irmodal-btn-confirm" 
              onClick={() => {
                onConfirmModification();
                onClose();
              }}
              style={{
                background: '#f59e0b',
                color: 'white',
                border: '3px solid #d97706',
                padding: '14px 28px',
                borderRadius: '12px',
                fontWeight: '700',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                letterSpacing: '0.025em',
                boxShadow: '0 4px 8px rgba(245, 158, 11, 0.3)',
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.05)';
                e.target.style.boxShadow = '0 6px 15px rgba(245, 158, 11, 0.4)';
                e.target.style.background = '#d97706';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 4px 8px rgba(245, 158, 11, 0.3)';
                e.target.style.background = '#f59e0b';
              }}
            >
              ⚡ {defaultModificationText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImpactRadarModal;
